import type { IconProps } from '@phosphor-icons/react';
import type { Translate } from '@client-app/i18n';
import type { IntegrationPermission } from '@common/api/featureService';
import {
    CalendarBlank,
    ChartBar,
    ChatCircleText,
    Database,
    FileText,
    Kanban,
    ListChecks,
    Package,
    Percent,
    ShoppingBag,
    ShoppingCart,
    Storefront,
    Table,
    Users
} from '@phosphor-icons/react';
import type { ComponentType } from 'react';

// ─── Base Types ────────────────────────────────────────────────

export type AccessLevel = 'no_access' | 'read' | 'action';

// ─── Capability Types ─────────────────────────────────────────

export interface CapabilityItem {
    id: string;
    label: string;
    type: 'read' | 'write';
}

/** A unified module section that contains both read and write capabilities */
export interface ModuleSection {
    id: string;
    title: string;
    icon: ComponentType<IconProps>;
    items: CapabilityItem[];
}

/** Per-platform capability catalog: array of unified module sections */
export type PlatformModuleCatalog = ModuleSection[];

// ─── Permission Types ─────────────────────────────────────────

/** Per-module access within a connection */
export interface ModuleAccessEntry {
    moduleId: string;
    access: AccessLevel;
}

export interface ConnectionPermission {
    connectionId: string;
    connectionName: string;
    connectionLabel?: string | null;
    platform: string;
    brandName: string;
    capabilityCatalog?: PlatformModuleCatalog;
    /** Per-module access levels. Each entry maps to a ModuleSection.id */
    moduleAccess: ModuleAccessEntry[];
}

export interface RolePermissions {
    connections: ConnectionPermission[];
    canManageWorkspaceSettings: boolean;
    canManageMembers: boolean;
    canManageRoles: boolean;
    canManageIntegrations: boolean;
    canManageConnectionAccess: boolean;
    canManageBilling: boolean;
    canViewUsage: boolean;
    canViewOutcomeSummary: boolean;
    canManageOutcomeSummary: boolean;
    canViewAuditLog: boolean;
    canTransferOwnership: boolean;
}

export interface Role {
    id: string;
    name: string;
    description: string;
    roleKey?: string | null;
    isSystem: boolean;
    isEditable: boolean;
    isDeletable?: boolean;
    memberCount: number;
    permissions: RolePermissions;
}

// ─── Helpers ──────────────────────────────────────────────────

const r = (id: string, label: string): CapabilityItem => ({
    id, label, type: 'read'
});
const w = (id: string, label: string): CapabilityItem => ({
    id, label, type: 'write'
});

// ─── Platform Module Catalogs ─────────────────────────────────

export const SHOPIFY_MODULES: PlatformModuleCatalog = [
    {
        id: 'store', title: 'Store', icon: Storefront,
        items: [
            r('store:view-information', 'View store information'),
        ],
    },
    {
        id: 'products', title: 'Products', icon: ShoppingBag,
        items: [
            r('products:browse-catalog', 'Browse and search product catalog'),
            r('products:view-variants', 'View product variant details (SKU, price, inventory)'),
            r('products:analyze-performance', 'Analyze product performance'),
            w('products:create-update-delete', 'Create, update, and delete products'),
            w('products:upload-images', 'Upload product images'),
            w('products:publish-channels', 'Publish or unpublish products to sales channels'),
            w('products:update-variants', 'Update product variants (price, SKU)'),
        ],
    },
    {
        id: 'orders', title: 'Orders', icon: ShoppingCart,
        items: [
            r('orders:query-status', 'Query orders and their statuses'),
            r('orders:view-line-items', 'View order line items'),
            r('orders:analyze-trends', 'Analyze order trends (revenue, AOV, multi-currency)'),
            w('orders:update-details', 'Update order details'),
            w('orders:edit-line-items', 'Edit order line items (modify quantities)'),
        ],
    },
    {
        id: 'customers', title: 'Customers', icon: Users,
        items: [
            r('customers:query-profiles', 'Query customer profiles and purchase history'),
            r('customers:analyze-segments', 'Analyze customer segments and cohorts'),
            w('customers:update-information', 'Update customer information'),
        ],
    },
    {
        id: 'inventory', title: 'Inventory', icon: Package,
        items: [
            r('inventory:check-levels', 'Check inventory levels'),
            w('inventory:adjust-levels', 'Adjust inventory levels'),
        ],
    },
    {
        id: 'discounts', title: 'Discounts', icon: Percent,
        items: [
            r('discounts:view-codes', 'View discounts and promo codes'),
            w('discounts:create-update-delete', 'Create, update, and delete discount codes'),
        ],
    },
    {
        id: 'analytics', title: 'Analytics', icon: ChartBar,
        items: [
            r('analytics:analyze-pixel-events', 'Analyze pixel event traffic (page views, checkout events)'),
        ],
    },
];

export const MARKETING_MODULES: PlatformModuleCatalog = [
    {
        id: 'ads_data', title: 'Advertising data', icon: ChartBar,
        items: [
            r('ads:read-performance', 'Read ad campaign performance data'),
            r('ads:read-spend', 'Access ad spend and budget information'),
            r('ads:read-insights', 'Analyze audience insights and conversion metrics'),
        ],
    },
    {
        id: 'account_info', title: 'Account information', icon: Users,
        items: [
            r('ads:view-account-details', 'View ad account profile and settings'),
        ],
    },
    {
        id: 'campaign_management', title: 'Campaign management', icon: ChartBar,
        items: [
            w('ads:create-campaign', 'Create and launch ad campaigns'),
            w('ads:edit-campaign', 'Edit campaign settings and targeting'),
            w('ads:pause-resume', 'Pause or resume active campaigns'),
        ],
    },
    {
        id: 'budget_control', title: 'Budget & bidding', icon: Database,
        items: [
            w('ads:adjust-budget', 'Adjust campaign budgets and bid strategies'),
            w('ads:set-schedule', 'Set ad scheduling and delivery preferences'),
        ],
    },
];

export const ANALYTICS_MODULES: PlatformModuleCatalog = [
    {
        id: 'analytics_reports', title: 'Analytics reports', icon: ChartBar,
        items: [
            r('analytics:view-reports', 'Access website traffic and behavior reports'),
            r('analytics:view-conversions', 'View goal completions and conversion data'),
        ],
    },
    {
        id: 'site_data', title: 'Site data', icon: Database,
        items: [
            r('analytics:view-properties', 'View web properties and view configurations'),
        ],
    },
];

const PERMISSION_GROUP_ICONS: Record<string, ComponentType<IconProps>> = {
    store: Storefront,
    products: ShoppingBag,
    orders: ShoppingCart,
    customers: Users,
    inventory: Package,
    discounts: Percent,
    analytics: ChartBar,
    campaigns: ChartBar,
    accounts: Database,
    messages: ChatCircleText,
    chats: Users,
    comments: ChatCircleText,
    contacts: Users,
    files: FileText,
    calendar: CalendarBlank,
    docs: FileText,
    sheets: Table,
    bitable: Kanban,
    tasks: ListChecks,
    account: Database,
    libraries: Kanban,
    variables: Database,
    dev_resources: FileText,
    webhooks: Database,
};

const BRAND_TO_PERMISSION_PLATFORM: Record<string, string> = {
    shopify: 'SHOPIFY',
    'judge.me': 'JUDGEME',
    google_ads: 'GOOGLE_ADS',
    meta_ads: 'META_ADS',
    facebook_ads: 'FACEBOOK_ADS',
    facebook_page: 'FACEBOOK_PAGE',
    instagram: 'INSTAGRAM',
    linkedin_ads: 'LINKEDIN_ADS',
    pinterest_ads: 'PINTEREST_ADS',
    x_ads: 'X_ADS',
    tiktok_ads: 'TIKTOK_ADS',
    youtube_ads: 'YOUTUBE',
    snapchat_ads: 'SNAPCHAT',
    google_analytics: 'GOOGLE_ANALYTICS',
    google_search_console: 'GOOGLE_SEARCH_CONSOLE',
    bigcommerce: 'BIGCOMMERCE',
    woocommerce: 'WOOCOMMERCE',
    magento: 'MAGENTO',
    prestashop: 'PRESTASHOP',
    wix: 'WIX',
    klaviyo: 'KLAVIYO',
    semrush: 'SEMRUSH',
    mailchimp: 'MAILCHIMP',
    okendo: 'OKENDO',
    feishu: 'FEISHU',
    figma: 'FIGMA',
};

const normalizeBrandKey = (brandName: string) =>
    brandName.trim().toLowerCase().replace(/[-\s]+/g, '_');

export const resolveIntegrationPermissionPlatform = (brandName: string) => {
    const normalized = normalizeBrandKey(brandName);
    return BRAND_TO_PERMISSION_PLATFORM[normalized] || normalized.toUpperCase();
};

export const buildModuleCatalogFromIntegrationPermissions = (
    brandName: string,
    integrationPermissions: IntegrationPermission[] = [],
    _t?: Translate,
): PlatformModuleCatalog => {
    const platform = resolveIntegrationPermissionPlatform(brandName);
    const sections = new Map<string, ModuleSection>();

    integrationPermissions
        .filter(permission =>
            permission.platform === platform
            && String(permission.status || '').toUpperCase() === 'ACTIVE')
        .sort((left, right) => (
            (left.groupSortOrder ?? 100) - (right.groupSortOrder ?? 100)
            || (left.sortOrder ?? 100) - (right.sortOrder ?? 100)
        ))
        .forEach((permission) => {
            const sectionId = permission.groupKey || 'general';
            let section = sections.get(sectionId);

            if (!section) {
                section = {
                    id: sectionId,
                    title: permission.groupTitle || permission.groupKey || sectionId,
                    icon: PERMISSION_GROUP_ICONS[sectionId] || Database,
                    items: [],
                };
                sections.set(sectionId, section);
            }

            section.items.push({
                id: permission.permissionKey,
                label: permission.label || permission.permissionKey,
                type: String(permission.action || '').toUpperCase() === 'WRITE' ? 'write' : 'read',
            });
        });

    return Array.from(sections.values());
};

export const COMMERCE_MODULES: PlatformModuleCatalog = [
    {
        id: 'products', title: 'Products', icon: ShoppingBag,
        items: [
            r('products:read', 'Access product catalog and inventory'),
        ],
    },
    {
        id: 'orders', title: 'Orders', icon: ShoppingCart,
        items: [
            r('orders:read', 'View order history and status'),
        ],
    },
];

/**
 * Resolve module catalog for a given brand.
 * Single source of truth shared between Integrations and Roles.
 */
export const getModuleCatalogForBrand = (brandName: string): PlatformModuleCatalog => {
    const name = brandName.toLowerCase();
    if (name === 'shopify') return SHOPIFY_MODULES;
    if (
        name.includes('ads')
        || name.includes('meta')
        || name.includes('facebook')
        || name.includes('instagram')
        || name.includes('mailchimp')
        || name.includes('tiktok')
        || name.includes('klaviyo')
    )
        return MARKETING_MODULES;
    if (name.includes('analytics') || name.includes('search console') || name.includes('semrush'))
        return ANALYTICS_MODULES;
    if (name.includes('figma'))
        return buildModuleCatalogFromIntegrationPermissions(brandName);
    if (name.includes('commerce') || name.includes('woo') || name.includes('magento') || name.includes('prestashop') || name.includes('wix') || name.includes('bigcommerce'))
        return COMMERCE_MODULES;
    return [];
};

/** Count total capability items across all modules */
export const countModuleItems = (catalog: PlatformModuleCatalog) =>
    catalog.reduce((sum, m) => sum + m.items.length, 0);

/** Build default moduleAccess for a brand – all modules set to given access */
export const buildDefaultModuleAccess = (
    brandName: string,
    defaultAccess: AccessLevel,
    integrationPermissions?: IntegrationPermission[],
    t?: Translate,
): ModuleAccessEntry[] => {
    if (integrationPermissions) {
        const catalog = buildModuleCatalogFromIntegrationPermissions(brandName, integrationPermissions, t);
        return catalog.length > 0
            ? catalog.map(m => ({ moduleId: m.id, access: defaultAccess }))
            : [{ moduleId: 'connection', access: defaultAccess }];
    }

    const catalog = getModuleCatalogForBrand(brandName);
    return catalog.map(m => ({ moduleId: m.id, access: defaultAccess }));
};

/** Get the access level for a specific module in a connection */
export const getModuleAccess = (conn: ConnectionPermission, moduleId: string): AccessLevel => {
    const entry = conn.moduleAccess.find(e => e.moduleId === moduleId);
    return entry?.access ?? 'no_access';
};

/** Count how many modules have at least Read access */
export const countActiveModules = (conn: ConnectionPermission): number =>
    conn.moduleAccess.filter(e => e.access !== 'no_access').length;

// ─── Connection Category Mapping ──────────────────────────────

export type ConnectionCategory = 'Store ops' | 'Marketing' | 'Analytics' | 'Other';

const BRAND_TO_CATEGORY: Record<string, ConnectionCategory> = {
    'Shopify': 'Store ops',
    'BigCommerce': 'Store ops',
    'WooCommerce': 'Store ops',
    'Magento': 'Store ops',
    'Wix': 'Store ops',
    'Google Ads': 'Marketing',
    'Meta Ads': 'Marketing',
    'Facebook Page': 'Marketing',
    'Instagram': 'Marketing',
    'Mailchimp': 'Marketing',
    'TikTok Ads': 'Marketing',
    'Klaviyo': 'Marketing',
    'Google Analytics': 'Analytics',
    'Google Search Console': 'Analytics',
    'Semrush': 'Analytics',
    'Figma': 'Other',
};

export const getCategoryForBrand = (brandName: string): ConnectionCategory =>
    BRAND_TO_CATEGORY[brandName] || 'Other';

export const CATEGORY_ORDER: ConnectionCategory[] = ['Store ops', 'Marketing', 'Analytics', 'Other'];

export const groupConnectionsByBrand = (connections: ConnectionPermission[]) => {
    const groups = new Map<string, ConnectionPermission[]>();
    for (const conn of connections) {
        const brand = conn.brandName;
        if (!groups.has(brand)) {
            groups.set(brand, []);
        }
        groups.get(brand)!.push(conn);
    }

    // Keep Shopify first, then sort the remaining brands alphabetically.
    const sortedBrands = Array.from(groups.keys()).sort((a, b) => {
        const aIsShopify = a.toLowerCase() === 'shopify';
        const bIsShopify = b.toLowerCase() === 'shopify';

        if (aIsShopify && !bIsShopify) {
            return -1;
        }

        if (!aIsShopify && bIsShopify) {
            return 1;
        }

        return a.localeCompare(b);
    });

    return sortedBrands.map(brand => ({
        brand,
        connections: groups.get(brand)!
    }));
};
